// Quita tildes/diacríticos y pasa a minúsculas, para que buscar "maria"
// encuentre "María" y viceversa.
export const normalizarTexto = (s) =>
  String(s).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// ¿El partido menciona este texto? Mira tanto el nombre del equipo/jugador
// individual (local_nombre/visitante_nombre) como el roster de jugadores
// inscritos en cada lado -- así una búsqueda por un nombre propio encuentra
// también sus partidos de dobles, donde el nombre visible es el del equipo,
// no el suyo. Se usa el roster (quién está inscrito), no la alineación real
// del partido, para que también aparezcan los partidos aún no disputados.
export const partidoContieneTexto = (partido, term, rosterPorParticipante) => {
  if (!term) return true;
  const t = normalizarTexto(term);
  const nombresEquipo = [partido.local_nombre, partido.visitante_nombre];
  const nombresJugadores = [
    ...(rosterPorParticipante[partido.local_participante_id] || []),
    ...(rosterPorParticipante[partido.visitante_participante_id] || []),
  ].flatMap((j) => [j.nombre, j.alias]).filter(Boolean);
  return [...nombresEquipo, ...nombresJugadores].some((n) => normalizarTexto(n).includes(t));
};

// Un aplazado con justificante aprobado por el comité (aplazado_justificado) sigue
// siendo de su jornada, pero se muestra además en la siguiente con la marca
// «aplazado». Solo visual: devuelve copias con `jornada` = la siguiente y
// `arrastradoDe` = la jornada real; el original no se toca. Si la siguiente
// jornada no existe en el calendario, no se arrastra a ninguna parte.
export const conArrastrados = (partidos) => {
  const existentes = new Set(partidos.map((p) => p.jornada));
  const arrastrados = partidos
    .filter((p) => p.aplazado_justificado && existentes.has(p.jornada + 1))
    .map((p) => ({ ...p, jornada: p.jornada + 1, arrastradoDe: p.jornada }));
  return arrastrados.length === 0 ? partidos : [...partidos, ...arrastrados];
};

// Cuántas jugadoras hay en pista a la vez: 1 en individual, 2 en dobles.
// No lo da /calendario-detalle (solo el nombre de la modalidad), pero es
// una regla fija del reglamento (ver modalidades.jugadores_en_pista).
export const jugadoresEnPista = (modalidad) => (modalidad.startsWith('INDIVIDUAL') ? 1 : 2);

// ¿Este jugador pertenece al participante (equipo o jugador individual) de
// alguno de los dos lados del partido? Se mira el roster de inscritos, no
// la alineación real del partido: así "mis partidos" incluye también los
// que aún no se han jugado (la alineación de un partido futuro está vacía).
export const esPartidoDeJugador = (partido, jugadorId, rosterPorParticipante) => {
  if (jugadorId == null) return false;
  const perteneceA = (participanteId) =>
    (rosterPorParticipante[participanteId] || []).some((j) => j.jugadorId === jugadorId);
  return perteneceA(partido.local_participante_id) || perteneceA(partido.visitante_participante_id);
};

// Puntos de clasificación por jugador y jornada, acumulados hasta
// `hastaJornada` inclusive. Replica la misma lógica que v_ranking_jugadores
// en el backend (puntos completos del partido a cada jugador alineado, sin
// repartir entre compañeros de dobles), pero desglosada jornada a jornada
// en vez de colapsada en un total de temporada.
export const calcularRankingAcumulado = (partidos, hastaJornada) => {
  const jornadas = Array.from({ length: hastaJornada }, (_, i) => i + 1);
  const porJugador = {};

  partidos
    .filter((p) => p.estado === 'CONFIRMADO' && p.jornada >= 1 && p.jornada <= hastaJornada)
    .forEach((p) => {
      const acumular = (alineacion, puntos) => {
        (alineacion || []).forEach((j) => {
          if (!porJugador[j.jugadorId]) {
            porJugador[j.jugadorId] = { jugadorId: j.jugadorId, nombre: j.nombre, alias: j.alias ?? null, porJornada: {} };
          }
          const fila = porJugador[j.jugadorId];
          fila.porJornada[p.jornada] = (fila.porJornada[p.jornada] || 0) + (puntos ?? 0);
        });
      };
      acumular(p.alineacion_local, p.clasif_local);
      acumular(p.alineacion_visitante, p.clasif_visitante);
    });

  const filas = Object.values(porJugador)
    .map((f) => {
      const porJornada = jornadas.map((n) => f.porJornada[n] || 0);
      const total = porJornada.reduce((a, b) => a + b, 0);
      return { jugadorId: f.jugadorId, nombre: f.nombre, alias: f.alias, porJornada, total };
    })
    .sort((a, b) => b.total - a.total || a.nombre.localeCompare(b.nombre));

  return { jornadas, filas };
};

// Incomparecencias que se muestran en público, solo con el partido confirmado (igual que el
// marcador). WO_LESION y demás incidencias no: el motivo (p. ej. una lesión) no debe salir
// en un endpoint público.
const INCIDENCIAS_WO = {
  WO_INCOMPARECENCIA: 'WO',
  WO_FUERA_DE_PLAZO: 'WO',
  SIN_AVISO: 'WO sin aviso',
};

export const etiquetaWo = (partido) =>
  partido.estado === 'CONFIRMADO' ? (INCIDENCIAS_WO[partido.incidencia] ?? null) : null;

const ETIQUETA_INCIDENCIA = {
  WO_LESION: 'WO por lesión',
  WO_INCOMPARECENCIA: 'WO',
  WO_FUERA_DE_PLAZO: 'WO fuera de plazo',
  WO_SANCION: 'WO por sanción',
  NO_DISPUTADO: 'No disputado',
  SIN_AVISO: 'WO sin aviso',
  INTERRUMPIDO: 'Interrumpido',
};

// Motivos por los que un partido va a la sección «WO / aplazados / descalificados»
// del acta (vacío = partido normal). Solo para gestión: a diferencia de etiquetaWo,
// no oculta la lesión ni exige que el partido esté confirmado.
export const motivosApartadoActa = (partido, descalificados) => [
  partido.estado === 'APLAZADO' && 'Aplazado',
  partido.incidencia && (ETIQUETA_INCIDENCIA[partido.incidencia] ?? partido.incidencia),
  descalificados.has(partido.local_participante_id) && `Descalificado: ${partido.local_nombre}`,
  descalificados.has(partido.visitante_participante_id) && `Descalificado: ${partido.visitante_nombre}`,
].filter(Boolean);

// Reparte los partidos de una jornada en las tres secciones del acta:
//  - programados: los de la jornada que se juegan en su cuadrante (colocados o aún sin colocar);
//  - adelantados: partidos de jornadas posteriores colocados en el cuadrante de esta;
//  - apartados: los de la jornada con WO/incidencia, aplazados o con un participante
//    descalificado, aunque ya se hayan jugado.
// `cuadrante` son los partidos del cuadrante de la jornada (GET .../pistas), que sabe qué
// partidos se adelantaron; los de esta jornada adelantados a otra anterior no salen aquí.
// `descalificados` es un Set de participante_id.
export const clasificarActa = (partidos, jornada, cuadrante, descalificados) => {
  const porId = new Map(partidos.map((p) => [p.id, p]));
  const apartados = partidos
    .filter((p) => p.jornada === jornada)
    .map((p) => ({ ...p, motivos: motivosApartadoActa(p, descalificados) }))
    .filter((p) => p.motivos.length > 0);
  const idsApartados = new Set(apartados.map((p) => p.id));

  const porHoraYPista = (a, b) =>
    (a.hora_inicio ?? '99:99').localeCompare(b.hora_inicio ?? '99:99') || (a.pista ?? 99) - (b.pista ?? 99);
  const delCuadrante = [...cuadrante].sort(porHoraYPista).filter((c) => !idsApartados.has(c.id) && porId.has(c.id));

  return {
    programados: delCuadrante.filter((c) => c.jornada === jornada).map((c) => porId.get(c.id)),
    adelantados: delCuadrante.filter((c) => c.jornada > jornada).map((c) => porId.get(c.id)),
    apartados,
  };
};
